// Copyright (c) Docugami, Inc. All rights reserved.

import { Config } from "config";
import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { createDgBaseQuery } from "store/baseQuery";
import { createEntityAdapter, createSelector, EntityState, nanoid } from "@reduxjs/toolkit";
import type { AppState } from "store";
import { Sample, SampleCatalogResponse, SampleResponse, SampleStatus } from "./samplesApi";
import { ClusterInfo } from "networking/api";

export const sampleAdapter = createEntityAdapter<Sample>({
    selectId: x => x.id,
    sortComparer: (a, b) => a.name.localeCompare(b.name)
});

export const {
    selectAll: selectAllSamples
} = sampleAdapter.getSelectors();

const orgSampleAdapter = createEntityAdapter<SampleResponse>({
    selectId: x => x.id,
    sortComparer: (a, b) => a.name.localeCompare(b.name)
});

export const {
    selectAll: selectAllOrgSamples
} = orgSampleAdapter.getSelectors();

const samplesBaseQuery = createDgBaseQuery(`${Config.publicApiBaseUrl}`);

export const samplesApiSlice = createApi({
    reducerPath: 'samplesApi',
    tagTypes: ["samplesCatalogue", "orgSamples"],
    baseQuery: samplesBaseQuery,
    endpoints: builder => ({
        getCatalogSamples: builder.query<SampleCatalogResponse[], void>({
            query: () => '/sample-catalog',
            providesTags: ['samplesCatalogue'],
            transformResponse: (response: { samples: SampleCatalogResponse[] }, meta, arg) => {
                return response.samples;
            }
        }),
        getOrganizationSamples: builder.query<EntityState<SampleResponse>, void>({
            query: () => ({
                url: '/samples',
                params: { legacy: true }
            }),
            providesTags: ['orgSamples'],
            transformResponse: (response: { samples: SampleResponse[] }, meta, arg) => {
                return orgSampleAdapter.setAll(orgSampleAdapter.getInitialState(), response.samples);
            }
        }),
        getSample: builder.query<Sample, string>({
            query: sampleId => `/samples/${encodeURIComponent(sampleId)}`,
        }),
        installSample: builder.mutation<string, string>({
            query: sampleId => ({
                url: '/samples',
                method: 'POST',
                body: {
                    catalogId: sampleId
                }
            }),
            invalidatesTags: ['orgSamples'],
            onQueryStarted: async (sampleId, api) => {
                // Update the request sample to have status installing
                const patchUpdate = api.dispatch(
                    samplesApiSlice.util.updateQueryData(
                        'getOrganizationSamples',
                        undefined,
                        orgSamples => {
                            const existingSample = selectAllOrgSamples(orgSamples).find(x => x.catalog?.id === sampleId);
                            if (!existingSample) {
                                const catalogSample = samplesApiSlice.endpoints.getCatalogSamples.select()(api.getState()).data?.find(x => x.id === sampleId);
                                if (!catalogSample) {
                                    return;
                                }

                                orgSampleAdapter.addOne(orgSamples, {
                                    id: nanoid(),
                                    catalog: {
                                        id: sampleId
                                    },
                                    name: catalogSample.name,
                                    description: catalogSample.description,
                                    version: '1',
                                    status: SampleStatus.Installing
                                })
                            } else {
                                orgSampleAdapter.updateOne(orgSamples, { id: existingSample.id, changes: { status: SampleStatus.Installing } });
                            }
                        }
                    )
                );

                try {
                    await api.queryFulfilled;
                } catch (error) {
                    patchUpdate.undo();
                }
            }
        }),
    })
});

export const {
    useGetCatalogSamplesQuery,
    useGetOrganizationSamplesQuery,
    useGetSampleQuery,
    useInstallSampleMutation
} = samplesApiSlice

export const selectGetCatalogSamplesResponse = (state: AppState) => samplesApiSlice.endpoints.getCatalogSamples.select()(state).data
export const selectGetOrganizationSamplesResponseQuery = (state: AppState) => samplesApiSlice.endpoints.getOrganizationSamples.select()(state)

export const createSelectGetSamplesState = () => createSelector(
    [
        (state: AppState) => selectGetCatalogSamplesResponse(state),
        (state: AppState) => selectGetOrganizationSamplesResponseQuery(state).data,
    ],
    (catalogSamples?: SampleCatalogResponse[], orgSamples?: EntityState<SampleResponse>): EntityState<Sample> => {
        if (!catalogSamples || !orgSamples) {
            return sampleAdapter.getInitialState();
        }

        return sampleAdapter.setAll(sampleAdapter.getInitialState(),
            catalogSamples.map(catalogSample => {
                const orgSample = selectAllOrgSamples(orgSamples).find(x => x.catalog?.id === catalogSample.id);
                return ({
                    id: catalogSample.id,
                    name: catalogSample.name,
                    description: catalogSample.description,
                    industries: catalogSample.industries,
                    status: !!orgSample ? orgSample.status : SampleStatus.Uninstalled
                })
            })
        )
    }
)

export const createSelectOrgSampleClusters = () => createSelector(
    [
        (state: AppState) => selectGetOrganizationSamplesResponseQuery(state).data,
        (state: AppState) => state.clusters.indexed
    ],
    (orgSamples, allClusters): ClusterInfo[] => {
        if (!orgSamples) {
            return [];
        }

        return selectAllOrgSamples(orgSamples).map(x => allClusters[x.id]?.clusterInfo).filter(x => !!x) ?? [];
    }
)
